import {
  addPileToCurrentOrder,
  setDefaultPiles,
  updateCurrentOrderPile,
} from "Redux/features/pile";
import { AppThunk } from "Redux/store";
import { isAxiosResponse } from "Services/index";
import {
  postOrderItem,
  updateOrderItem,
  getItemsCount,
} from "Services/order";
import { getPiles } from "Services/pile";
import { getMedia } from "Services/media";
import { PILE_SUBCLASS_TYPE } from "Constants/pile-constants";

// NOTE:
// rowNum === 0 when creating a new item.
// rowNum > 0 when duplicating an existing item.
export const createPile =
  (
    pile: any,
    orderItem: any,
    next = (_val: boolean) => {},
    rowNum: number,
    orderId = 0
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const user = getState().user.userData;

      /*
       * Logged-in flow
       */
      if (user) {
        const countResponse = await getItemsCount(
          PILE_SUBCLASS_TYPE,
          orderId
        );

        if (!isAxiosResponse(countResponse)) {
          throw new Error("קבלת מספר השורה הבא נכשלה.");
        }

        const preparedOrderItem = {
          ...orderItem,
          row_number:
            rowNum > 0
              ? rowNum
              : countResponse.data.row_number + 1,
        };

        const response = await postOrderItem({
          ...preparedOrderItem,
          ...(orderId > 0 ? { order: orderId } : {}),
          tool: pile,
          user: user.id,
        });

        if (!isAxiosResponse(response)) {
          throw new Error("שמירת הכלונס נכשלה.");
        }

        const previewID = response.data.tool.preview_image;
        const mediaResponse = await getMedia(previewID);

        if (!isAxiosResponse(mediaResponse)) {
          throw new Error(
            "טעינת תמונת הכלונס לאחר השמירה נכשלה."
          );
        }

        const orderIt = {
          ...response.data,
          tool: {
            ...response.data.tool,
            preview_image: {
              ...mediaResponse.data,
            },
          },
        };

        const piles = getState().pile.currentOrderPiles;
        const lastPile =
          piles.length > 0 ? piles[piles.length - 1] : null;

        const shouldAdd =
          piles.length === 0 ||
          response.data.row_number - lastPile.row_number === 1 ||
          response.data.row_number < lastPile.row_number;

        if (shouldAdd) {
          await dispatch(addPileToCurrentOrder(orderIt));
        }

        next(true);

        return {
          success: true,
          data: orderIt,
        };
      }

      /*
       * Offline / guest flow
       */
      const pileData = {
        ...orderItem,
        tool: pile,
      };

      const previewID = pileData.tool.preview_image;
      const mediaResponse = await getMedia(previewID);

      if (!isAxiosResponse(mediaResponse)) {
        throw new Error("טעינת תמונת הכלונס נכשלה.");
      }

      const orderIt = {
        ...pileData,
        tool: {
          ...pileData.tool,
          preview_image: {
            ...mediaResponse.data,
          },
        },
      };

      await dispatch(addPileToCurrentOrder(orderIt));

      next(true);

      return {
        success: true,
        data: orderIt,
      };
    } catch (error) {
      console.error("Failed to create pile:", error);

      next(false);

      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "שמירת הכלונס נכשלה.",
      };
    }
  };

export const updatePile =
  (
    orderItemID: number,
    pile: any,
    orderItem: any,
    next = (_val: boolean) => {}
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const user = getState().user.userData;

      if (!user) {
        throw new Error("לא נמצא משתמש מחובר.");
      }

      const response = await updateOrderItem(
        orderItemID,
        {
          ...orderItem,
          tool: pile,
        }
      );

      if (!isAxiosResponse(response)) {
        throw new Error("עדכון הכלונס נכשל.");
      }

      const previewID =
        response.data.tool.preview_image;

      const mediaResponse = await getMedia(previewID);

      if (!isAxiosResponse(mediaResponse)) {
        throw new Error(
          "טעינת תמונת הכלונס לאחר העדכון נכשלה."
        );
      }

      const orderIt = {
        ...response.data,
        tool: {
          ...response.data.tool,
          preview_image: {
            ...mediaResponse.data,
          },
        },
      };

      await dispatch(updateCurrentOrderPile(orderIt));

      next(true);

      return {
        success: true,
        data: orderIt,
      };
    } catch (error) {
      console.error("Failed to update pile:", error);

      next(false);

      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "עדכון הכלונס נכשל.",
      };
    }
  };

export const fetchDefaultsPiles =
  (): AppThunk =>
  async (dispatch) => {
    try {
      const shapes = await getPiles(2);

      if (Array.isArray(shapes) && shapes.length > 0) {
        await dispatch(setDefaultPiles(shapes));
      }

      return {
        success: true,
        data: shapes,
      };
    } catch (error) {
      console.error(
        "Failed to fetch default piles:",
        error
      );

      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "טעינת ברירות המחדל של הכלונסאות נכשלה.",
      };
    }
  };